import React from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { Card, HelpIcon, Link, Progress, useMatchBreakpoints, useModal } from '@nominex/dex-uikit'
import { SlButton, SlSkeleton } from '@shoelace-style/shoelace/dist/react'
import LoadableValue from 'components/LoadableValue'
import ClaimModal from 'components/Claim/ClaimModal'
import { useTranslation } from 'contexts/Localization'
import { useWagmi } from 'libs/wagmi'
import { AppState } from 'state'
import { useIsAuthenticated } from 'state/profile/hooks'
import { FetchStatus, ReferralLevel, State } from 'state/types'
import useTheme from 'hooks/useTheme'

const StyledTitleText = tw.div`
  leading-16 text-11 text-primary mb-4 opacity-60
`

const StyledCardCaption = tw.div`
  grid gap-12 sm:grid-cols-[repeat(2, 1fr)_max-content] grid-cols-2 pt-16 pb-12 pr-12 sm:pl-[140px] pl-16
`

const nextLevelCashbackMap = {
  [ReferralLevel.STARTER]: ReferralLevel.VIP,
  [ReferralLevel.PARTNER]: ReferralLevel.VIP,
  [ReferralLevel.PRO]: ReferralLevel.VIP,
  [ReferralLevel.VIP]: ReferralLevel.ELITE,
  [ReferralLevel.ELITE]: ReferralLevel.MAX,
  [ReferralLevel.MAX]: ReferralLevel.MAX,
}

const CashbackWidget: React.FC = () => {
  const { account } = useWagmi()
  const { theme } = useTheme()
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()
  const isAuthenticated = useIsAuthenticated()
  const [openClaimModal] = useModal(<ClaimModal />)

  const unclaimedCashback = useSelector((state: AppState) => state.affiliate.user.bonuses.cashback)
  const isUnclaimedCashbackLoaded = useSelector((state: AppState) =>
    [FetchStatus.FETCHED, FetchStatus.FAILED].includes(state.affiliate.user.bonuses.fetchStatus),
  )
  const totalEarnedCashback = useSelector((state: AppState) => state.affiliate.user.bonuses.totalEarned.cashback)
  const isTotalEarnedCashbackLoaded = useSelector((state: AppState) =>
    [FetchStatus.FETCHED, FetchStatus.FAILED].includes(state.affiliate.user.bonuses.totalEarned.fetchStatus),
  )

  const { farmingLevels } = useSelector((state: State) => state.affiliate)
  const { currentLevel, farmingLevelProgress, fetchStatus } = useSelector(
    (state: State) => state.affiliate.user.farmingLevel,
  )
  const [usdInvestedInFarming, minUsdAmountForNextLevel] = farmingLevelProgress

  const currentLevelCashback = farmingLevels.find(({ name }) => name === currentLevel)?.cashback
  const nextLevelCashback = farmingLevels.find(({ name }) => name === nextLevelCashbackMap[currentLevel])?.cashback

  const isDataReady = fetchStatus === FetchStatus.FETCHED
  const isMaxLevelReached = currentLevel === ReferralLevel.MAX

  const progress = isMaxLevelReached
    ? 100
    : minUsdAmountForNextLevel
      ? (usdInvestedInFarming / minUsdAmountForNextLevel) * 100
      : 0
  const progressLabel = isDataReady
    ? isMaxLevelReached
      ? t('Max level')
      : t('Your progress $%usdInvestedInFarming%/$%minUsdAmountForNextLevel%', {
          usdInvestedInFarming: usdInvestedInFarming.toFixed(0),
          minUsdAmountForNextLevel: minUsdAmountForNextLevel.toFixed(0),
        })
    : t('Loading...')

  const color = isMaxLevelReached ? theme.colors.success : theme.colors.blue500
  const background = isMobile
    ? 'linear-gradient(108.82deg, #3E56FF 33.45%, #0217D8 99.53%)'
    : "top left / 236px no-repeat url('/images/banners/cashback-bg.png'), linear-gradient(108.82deg, #3E56FF 33.45%, #0217D8 99.53%)"

  return (
    <div className="mb-24 w-full max-w-[570px]">
      <Card background={background} withoutBorder>
        <StyledCardCaption>
          <div>
            <StyledTitleText>{t('Total earned')}</StyledTitleText>
            <LoadableValue loading={!isTotalEarnedCashbackLoaded} height="14px" fontSize="14px" lineHeight={1} bold>
              {totalEarnedCashback} NMX
            </LoadableValue>
          </div>

          <div>
            <StyledTitleText>{t('Unclaimed')}</StyledTitleText>
            <LoadableValue loading={!isUnclaimedCashbackLoaded} height="14px" fontSize="14px" lineHeight={1} bold>
              {unclaimedCashback} NMX
            </LoadableValue>
          </div>

          <SlButton
            className="sm:col-auto col-span-2"
            size="small"
            variant="success"
            onClick={openClaimModal}
            disabled={!isAuthenticated}
          >
            {t('Claim')}
          </SlButton>
        </StyledCardCaption>

        {account && (
          <Card
            m="0 12px 16px 12px"
            background="linear-gradient(108.82deg, #1E39F2 33.45%, #031299 99.53%)"
            withoutBorder
          >
            <div style={{ padding: '12px' }}>
              <div className="flex justify-between">
                <StyledTitleText>{t('Your cashback')}</StyledTitleText>
                {!isMaxLevelReached && <StyledTitleText>{t('Next cashback')}</StyledTitleText>}
              </div>

              <div className="flex justify-between">
                {isDataReady ? (
                  <div className="text-primary text-14 font-semibold">
                    {currentLevelCashback} %{isMaxLevelReached ? <>&nbsp; {t('Maximum cashback')}</> : ''}
                  </div>
                ) : (
                  <SlSkeleton className="w-[42px] h-[20px]" effect="sheen" />
                )}
                {!isMaxLevelReached &&
                  (isDataReady ? (
                    <div className="text-primary text-14 font-semibold">{nextLevelCashback} %</div>
                  ) : (
                    <SlSkeleton className="w-[42px] h-[20px]" effect="sheen" />
                  ))}
              </div>

              <div className="my-12">
                <Progress
                  label={progressLabel}
                  color={color}
                  variant="semi-flat"
                  primaryStep={progress}
                  loading={!isDataReady}
                />
              </div>

              <div className="flex justify-between">
                <div className="flex">
                  <HelpIcon width="12px" />
                  <div className="text-primary text-12 ml-4">
                    {t('The amount is approximate, will be calculated at the time of deposit')}
                  </div>
                </div>

                <Link
                  href="https://nomiswap.gitbook.io/home/core-products/exchange-swap-expand/trading-fees-cashback"
                  target="_blank"
                  rel="noopener noreferrer"
                  fontSize="12px"
                  color="blue500"
                  style={{ display: 'inline' }}
                >
                  {t('Learn more')}
                </Link>
              </div>
            </div>
          </Card>
        )}
      </Card>
    </div>
  )
}

export default CashbackWidget
